<template>
  <div class="print-view">
    <!-- Print controls menu -->
    <div class="print-controls-menu w-full mb-2">
      <div class="menu-start">
        <Button icon="pi pi-arrow-left" label="Back" @click="goBack" class="mr-2" />
      </div>

      <div class="menu-center">
        <div class="promo-control">
          <label for="include-promos" class="promo-label">Include Promotional Offer(s)</label>
          <ToggleSwitch v-model="includePromos" inputId="include-promos" />
        </div>
      </div>

      <div class="menu-end">
        <Select
          v-model="selectedTemplate"
          :options="templateOptions"
          optionLabel="name"
          optionValue="value"
          placeholder="Select Template"
          class="mr-2"
        />
        <Button
          icon="pi pi-envelope"
          label="Email"
          @click="emailTransaction"
          class="mr-2"
          :disabled="!partyEmail"
          v-tooltip="{
            value: !partyEmail ? 'No email found!' : '',
            showDelay: 500,
            hideDelay: 300,
          }"
        />
        <SplitButton
          icon="pi pi-print"
          label="Print"
          :model="printOptions"
          @click="defaultPrint"
          class="print-button"
        />
      </div>
    </div>

    <!-- Printable content wrapper -->
    <div class="printable-wrapper" ref="printableContent">
      <!-- Dynamic template rendering -->
      <component
        ref="printComponent"
        :is="currentTemplate"
        :transaction="transaction"
        :items="items"
        :partyName="partyName"
        :partyAddress="partyAddress"
        :partyContact="getPartyContact()"
        :includePromos="includePromos"
        class="isolate-styles"
      >
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { type TransactionRecord, type TransactionItem } from '@/types/Transaction'
import { useToast } from 'primevue/usetoast'
import { useCommodityTypeService } from '@/services/commodityTypeService'
import { getTransactionService } from '@/services/serviceFactory'
import { useLiveDocument } from '@/composables/useLiveDocument'
import type { DocumentReference } from 'firebase/firestore'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import Select from 'primevue/select'
import ToggleSwitch from 'primevue/toggleswitch'
import StandardTemplate from '@/components/print-templates/StandardTemplate.vue'
import A4Template from '@/components/print-templates/A4Template.vue'
import { TransactionModel } from '@/models/TransactionModel'
import { useEmailService } from '@/services/emailService'
import { useSettingsStore } from '@/stores/settings'
import juice from 'juice'
import { formatCurrency } from '@/utils/formatters'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const commodityTypeService = useCommodityTypeService()
const settingsStore = useSettingsStore()
const printableContent = ref<HTMLElement | null>(null)

// State
const transactionId = computed(() => route.params.id as string)
const transaction = ref<TransactionRecord>({} as TransactionRecord)
const items = ref<TransactionItem[]>([])
const loading = ref(true)
const selectedTemplate = ref('standard')
const includePromos = ref(false)
const templateOptions = [
  { name: 'Standard Receipt', value: 'standard' },
  { name: 'A4', value: 'a4' },
]

const partyEmail = ref<string | null>(null)
const partyName = ref<string>('')
const partyAddress = ref<string>('')

// Get transaction service and docref for live updates
const transactionServiceRef = ref<Awaited<ReturnType<typeof getTransactionService>> | null>(null)
const transactionDocRef = ref<DocumentReference<TransactionRecord> | null>(null)

// Update docref when service or transactionId changes
watch(
  [transactionServiceRef, transactionId],
  ([service, id]) => {
    if (service && id) {
      transactionDocRef.value = service.getDocRef(id)
    } else {
      transactionDocRef.value = null
    }
  },
  { immediate: true },
)

// Use live document for reactive updates
const { doc: liveTransactionDoc } = useLiveDocument(transactionDocRef, {
  label: 'transaction-print',
  log: true,
})

// Create a computed property for the transaction model
const transactionModel = computed(() => {
  return new TransactionModel(transaction.value)
})

// Update print options to include Star PassPRNT
const printOptions = computed(() => [
  {
    label: 'System Print',
    icon: 'pi pi-print',
    command: () => systemPrint(),
  },
  {
    label: import.meta.env.DEV ? 'Download HTML (Dev)' : 'Thermal Printer',
    icon: 'pi pi-print',
    command: () => thermalPrint(),
  },
  {
    label: 'DEV/TEST',
    icon: 'pi pi-cog',
    command: () => devTestPrint(),
  },
])

const currentTemplate = computed(() => {
  switch (selectedTemplate.value) {
    case 'a4':
      return A4Template
    default:
      return StandardTemplate
  }
})

// Helper function to get party contact info
function getPartyContact(): string {
  const tx = transaction.value
  if (!tx) return ''

  const parts = []
  const person = tx.personSnapshot
  const org = tx.organizationSnapshot

  if (person?.phone) parts.push(`Phone: ${person.phone}`)
  else if (org?.phone) parts.push(`Phone: ${org.phone}`)

  if (person?.email) parts.push(`Email: ${person.email}`)
  else if (org?.email) parts.push(`Email: ${org.email}`)

  return parts.join(' | ')
}

// Methods
function goBack() {
  router.push(`/transaction/${route.params.id}`)
}

function emailTransaction() {
  if (!partyEmail.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No email address available for this party',
      life: 5000,
    })
    return
  }

  if (printComponent.value) {
    const element = printComponent.value.$el
    const receiptHtml = element.innerHTML
    const emailService = useEmailService()
    const subject = `Transaction Receipt - ${transaction.value.transaction_number}`
    emailService
      .sendEmail({
        to: partyEmail.value,
        from: settingsStore.companySettings.email || '',
        message: {
          subject,
          html: receiptHtml,
        },
        personId: transaction.value.personId,
        organizationId: transaction.value.organizationId,
      })
      .then(() => {
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Receipt has been sent to ' + partyEmail.value,
          life: 5000,
        })
      })
      .catch((error) => {
        console.error('Failed to send email:', error)
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to send email. Please try again.',
          life: 5000,
        })
      })
  }
}

// Default print function - now launches Star PassPRNT
function defaultPrint() {
  thermalPrint()
}

// Enhanced print function with iOS support
function systemPrint() {
  try {
    if (printComponent.value) {
      const element = printComponent.value.$el
      const receiptHtml = element.innerHTML
      const inlineStyles = juice(receiptHtml)

      // Create an invisible iframe for printing
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      // Write the juiced content to the iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
      if (iframeDoc) {
        iframeDoc.open()
        iframeDoc.write(`
          <html>
            <head>
              <title>Print</title>
              <style>
                @media print {
                  body { margin: 0; padding: 0; }
                }
              </style>
            </head>
            <body>
              ${inlineStyles}
            </body>
          </html>
        `)
        iframeDoc.close()

        // Use a timeout to ensure the content is loaded
        setTimeout(() => {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()

          // Clean up the iframe after printing
          setTimeout(() => {
            document.body.removeChild(iframe)
          }, 1000)
        }, 500)
      }
    }
  } catch (error) {
    console.error('Error during system print:', error)
    toast.add({
      severity: 'error',
      summary: 'Printing Error',
      detail: 'Failed to print. Please try again.',
      life: 5000,
    })
  }
}

// New function for thermal printing with Star PassPRNT
async function thermalPrint() {
  try {
    // Get HTML from the rendered template component
    let htmlContent = ''

    if (printComponent.value) {
      const element = printComponent.value.$el
      htmlContent = element.outerHTML
    } else if (printableContent.value) {
      htmlContent = printableContent.value.innerHTML
    } else {
      throw new Error('No printable content found')
    }

    // Convert images to base64 for standalone HTML
    htmlContent = await convertImagesToBase64(htmlContent)

    // Apply juice to inline the styles
    const inlineStyles = juice(htmlContent)

    // Check if we're in development mode
    const isDevelopment =
      import.meta.env.DEV ||
      import.meta.env.MODE === 'development' ||
      window.location.hostname === 'localhost'

    if (isDevelopment) {
      // In development, download the HTML file instead of opening PassPRNT
      downloadHTMLFile(inlineStyles, `receipt-${transaction.value.transaction_number}.html`)

      toast.add({
        severity: 'info',
        summary: 'Development Mode',
        detail: 'HTML file downloaded instead of opening PassPRNT',
        life: 3000,
      })
    } else {
      // In production, use the original PassPRNT flow
      // Encode the HTML for the URL
      const encodedHTML = encodeURIComponent(inlineStyles)

      // Create the callback URL (current page)
      const callbackUrl = encodeURIComponent(window.location.href)

      // Create the Star PassPRNT URL
      const passPrntUrl = `starpassprnt://v1/print/nopreview?&back=${callbackUrl}&html=${encodedHTML}`

      // Open the URL to launch Star PassPRNT
      window.location.href = passPrntUrl

      // Show a toast message
      toast.add({
        severity: 'info',
        summary: 'Thermal Printing',
        detail: 'Launching Star PassPRNT for thermal printing...',
        life: 3000,
      })
    }
  } catch (error) {
    console.error('Error launching thermal printer:', error)
    toast.add({
      severity: 'error',
      summary: 'Printing Error',
      detail: 'Failed to launch thermal printer. Please try system print instead.',
      life: 5000,
    })
  }
}

// DEV/TEST function that uses generateThermalHTML instead of Vue template
async function devTestPrint() {
  try {
    // Generate HTML using the thermal HTML generator function
    const htmlContent = generateThermalHTML()

    // Apply juice to inline the styles
    const inlineStyles = juice(htmlContent)

    // Check if we're in development mode
    const isDevelopment =
      import.meta.env.DEV ||
      import.meta.env.MODE === 'development' ||
      window.location.hostname === 'localhost'

    if (isDevelopment) {
      // In development, download the HTML file instead of opening PassPRNT
      downloadHTMLFile(
        inlineStyles,
        `dev-test-receipt-${transaction.value.transaction_number}.html`,
      )

      toast.add({
        severity: 'info',
        summary: 'DEV/TEST Mode',
        detail: 'DEV/TEST HTML file downloaded instead of opening PassPRNT',
        life: 3000,
      })
    } else {
      // In production, use the PassPRNT flow
      // Encode the HTML for the URL
      const encodedHTML = encodeURIComponent(inlineStyles)

      // Create the callback URL (current page)
      const callbackUrl = encodeURIComponent(window.location.href)

      // Create the Star PassPRNT URL
      const passPrntUrl = `starpassprnt://v1/print/nopreview?&back=${callbackUrl}&html=${encodedHTML}`

      // Open the URL to launch Star PassPRNT
      window.location.href = passPrntUrl

      // Show a toast message
      toast.add({
        severity: 'info',
        summary: 'DEV/TEST Printing',
        detail: 'Launching Star PassPRNT for DEV/TEST thermal printing...',
        life: 3000,
      })
    }
  } catch (error) {
    console.error('Error launching DEV/TEST thermal printer:', error)
    toast.add({
      severity: 'error',
      summary: 'DEV/TEST Printing Error',
      detail: 'Failed to launch DEV/TEST thermal printer. Please try system print instead.',
      life: 5000,
    })
  }
}

// Generate HTML optimized for thermal printers with larger sizing
function generateThermalHTML() {
  return `<!DOCTYPE html>
<html>
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <title>Scrap Bros Receipt</title>
  <style>
    body {
      font-family: sans-serif;
      margin: 0;
      padding: 0;
      width: 100%;
      font-weight: bold;
    }
    .header {
      text-align: center;
      margin-bottom: 20px;
    }
    .company-name {
      font-size: 60px;
      font-weight: bold;
      margin: 0;
    }
    .company-info {
      font-size: 30px;
      margin: 10px 0;
    }
    .transaction-info {
      font-size: 30px;
      margin: 20px 0;
    }
    .transaction-info div {
      margin: 10px 0;
    }
    .divider {
      border: none;
      height: 5px;
      background-color: #000;
      margin: 25px 0;
    }
    .items-section {
      margin: 20px 0;
    }
    .item-entry {
      margin-bottom: 30px;
    }
    .item-name {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 10px;
    }
    .item-details {
      padding-left: 20px;
    }
    .item-row {
      display: flex;
      justify-content: space-between;
      font-size: 20px;
      margin: 8px 0;
    }
    .item-total {
      font-weight: bold;
    }
    .item-divider {
      border-top: 1px dashed #000;
      margin: 15px 0;
    }
    .totals {
      font-size: 36px;
      margin: 20px 0;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      margin: 10px 0;
    }
    .total-label {
      font-weight: bold;
    }
    .footer {
      text-align: center;
      margin-top: 30px;
      font-size: 30px;
    }
    .signature {
      margin-top: 30px;
    }
    .signature-label {
      font-size: 30px;
      margin-bottom: 10px;
    }
    .signature-image {
      max-width: 100%;
      height: 120px;
    }
  </style>
</head>
<body>
  <div class="header">
    <p class="company-name">Scrap Bros LLC</p>
    <p class="company-info">37423 W Highway 16, Bristow, OK 74010</p>
    <p class="company-info">Phone: (918) 426-9506</p>
  </div>

  <hr class="divider">

  <div class="transaction-info">
    <div><strong>Transaction #:</strong> ${transaction.value.transaction_number}</div>
    <div><strong>Date:</strong> ${formatDate(transaction.value.transaction_date)}</div>
    <div><strong>Type:</strong> ${transaction.value.transaction_type === 'purchase' ? 'Purchase' : 'Sale'}</div>
    <div><strong>Party:</strong> ${partyName.value}</div>
  </div>

  <hr class="divider">

  <div class="items-section">
    <div style="font-size: 36px; font-weight: bold; margin-bottom: 15px;">ITEMS</div>
    ${items.value
      .map(
        (item) => `
    <div class="item-entry">
      <div class="item-name">${getCommodityName(item)}</div>
      <div class="item-details">
        <div class="item-row">
          <span>Qty:</span>
          <span>Gross: ${item.weight?.gross?.toString() || '0.00'} lbs</span>
          <span>Tare: ${item.weight?.tare?.toString() || '0.00'} lbs</span>
          <span>Net WT: ${item.weight?.net?.toString() || '0.00'} lbs</span>
        </div>
        <div class="item-row">
          <span>Price:</span>
          <span>${formatPricePerLb(item.price_per_lb)}/lb</span>
        </div>
        <div class="item-row item-total">
          <span>Total:</span>
          <span>${formatCurrency(calculateLineTotal(item))}</span>
        </div>
      </div>
      <div class="item-divider"></div>
    </div>
    `,
      )
      .join('')}
  </div>

  <hr class="divider">

  <div class="totals">
    <div class="total-row">
      <span class="total-label">Total Weight:</span>
      <span>${transactionModel.value.totalWeight} lbs</span>
    </div>
    <div class="total-row">
      <span class="total-label">Total Amount:</span>
      <span>${formatCurrency(transaction.value.total_amount || 0)}</span>
    </div>
  </div>

  <hr class="divider">

  ${
    transaction.value.party_signature
      ? `
  <div class="signature">
    <div class="signature-label">Signature:</div>
    <img class="signature-image" width="100%" src="${transaction.value.party_signature.data_url}" alt="Signature">
  </div>
  `
      : ''
  }

  <div class="footer">
    <p>Thank you for your business!</p>
    <p v-if="settingsStore.companySettings.website">{{ settingsStore.companySettings.website }}</p>
  </div>
</body>
</html>`
}

// Helper function to get commodity name
function getCommodityName(item: TransactionItem): string {
  return item.commodity_name || 'Unknown Item'
}

// Helper function to format price per lb with dynamic precision
// At least 2 decimals, but more if there are more significant digits
function formatPricePerLb(price: number | string): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return '0.00'

  // Round to 6 decimal places first to eliminate floating point artifacts
  // This handles cases like 3.7 being stored as 3.70000000000000017764
  const rounded = Math.round(numPrice * 1000000) / 1000000

  // Check if it's effectively a whole number
  if (Math.abs(rounded - Math.round(rounded)) < 0.000001) {
    return Math.round(rounded).toFixed(2)
  }

  // Find the minimum precision needed by checking the rounded value
  // Start with 6 decimals and work backwards to find the actual precision
  let formatted = rounded.toFixed(6)

  // Remove trailing zeros, but ensure at least 2 decimals
  formatted = formatted.replace(/\.?0+$/, '')

  // Count decimal places
  const decimalIndex = formatted.indexOf('.')
  if (decimalIndex === -1) {
    // No decimal point, add .00
    return rounded.toFixed(2)
  }

  const decimalPlaces = formatted.length - decimalIndex - 1
  // Ensure at least 2 decimals
  if (decimalPlaces < 2) {
    return rounded.toFixed(2)
  }

  return formatted
}

// Helper function to calculate line total
function calculateLineTotal(item: TransactionItem): number {
  return (
    (parseFloat(item.price_per_lb.toString()) || 0) *
    parseFloat(item.weight?.net?.toString() || '0')
  )
}

// Helper function to format date
function formatDate(timestamp: Date | { toDate: () => Date } | null): string {
  if (!timestamp) return 'N/A'
  const date = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// Helper function to download HTML file
function downloadHTMLFile(htmlContent: string, filename: string) {
  const blob = new Blob([htmlContent], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Helper function to convert images to base64
async function convertImagesToBase64(htmlContent: string): Promise<string> {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')
  const images = doc.querySelectorAll('img')

  for (const img of images) {
    const src = img.getAttribute('src')
    if (src && !src.startsWith('data:')) {
      try {
        // Handle relative paths to assets
        let imageUrl = src
        if (src.startsWith('@/assets/')) {
          // Convert @/assets/ to the actual public path
          imageUrl = src.replace('@/assets/', '/src/assets/')
        }

        // Fetch the image and convert to base64
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const reader = new FileReader()

        await new Promise((resolve, reject) => {
          reader.onload = () => {
            img.setAttribute('src', reader.result as string)
            resolve(null)
          }
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.warn('Failed to convert image to base64:', src, error)
        // Keep the original src if conversion fails
      }
    }
  }

  return doc.documentElement.outerHTML
}

// Process transaction data when live document updates
async function processTransactionData(transactionData: TransactionRecord | null) {
  if (!transactionData) {
    loading.value = false
    return
  }

  loading.value = true
  try {
    // Load commodity types for lookup
    const commodityTypes = await commodityTypeService.getActiveCommodityTypes()

    // Process items
    if (transactionData.items && Array.isArray(transactionData.items)) {
      items.value = transactionData.items.map((item) => {
        let commodityName = 'Unknown'

        if (item.commodity_type_id) {
          const commodity = commodityTypes.find((c) => c.id === item.commodity_type_id)
          if (commodity) {
            commodityName = commodity.name
          }
        }

        return {
          ...item,
          commodity_name: commodityName,
        }
      })
    }

    transaction.value = {
      id: transactionId.value,
      ...transactionData,
    }

    // Update party info
    const party = await transactionModel.value.getParty()
    partyEmail.value = party.email
    partyName.value = party.name
    partyAddress.value = party.address
  } catch (error) {
    console.error('Error processing transaction:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load transaction details',
      life: 5000,
    })
  } finally {
    loading.value = false
  }
}

// Watch live document and update transaction state
watch(
  liveTransactionDoc,
  async (newDoc) => {
    if (newDoc) {
      await processTransactionData(newDoc as TransactionRecord)
    } else if (transactionDocRef.value) {
      // Doc ref is set but document is null - not found
      loading.value = false
    }
  },
  { immediate: true },
)

// Check URL parameters for PassPRNT callback
function checkPassPrntCallback() {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.has('passprnt_message')) {
    const status = urlParams.get('passprnt_message')
    if (status === 'SUCCESS') {
      toast.add({
        severity: 'success',
        summary: 'Printing Success',
        detail: 'Receipt was successfully sent to the printer.',
        life: 3000,
      })
      window.close()
    } else {
      toast.add({
        severity: 'error',
        summary: 'Printing Error',
        detail: 'Failed to print receipt. Please try again or use system print.',
        life: 5000,
      })
    }
  }
}

// Initialize transaction service on mount
onMounted(async () => {
  checkPassPrntCallback()
  try {
    transactionServiceRef.value = await getTransactionService()
  } catch (error) {
    console.error('Error initializing transaction service:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to initialize transaction service.',
      life: 5000,
    })
  }
})

const printComponent = ref<any>(null)
</script>

<style>
/* Base styles */
.print-view {
  font-family: Arial, sans-serif;
  color: #333;
  background-color: #f5f5f5;
  padding: 20px;
}

/* Print controls menu */
.print-controls-menu {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.menu-start {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.menu-center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.menu-end {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.promo-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.promo-label {
  font-size: 1.1rem;
  font-weight: 500;
}

.print-button {
  min-height: 44px; /* iOS minimum tap target size */
  touch-action: manipulation; /* Disable double-tap to zoom */
}

/* Printable wrapper - just a container, no styling that would affect print */
.printable-wrapper {
  margin: 0 auto;
}

/* Isolate component styles */
.isolate-styles {
  all: initial;
  display: block;
}

/* Print media styles */
@media print {
  body {
    background: none;
    margin: 0;
    padding: 0;
  }

  .print-view {
    background: none;
    padding: 0;
  }

  .print-controls-menu {
    display: none;
  }

  #vue-inspector-container {
    display: none;
  }
}
</style>
